import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {FileUpload} from "primereact/fileupload";
import {Knob} from "primereact/knob";
import {Message} from "primereact/message";
import Icon from "../../system/components/icon";
import {useTranslate} from "../../system/useTranslate";
import {fileThumbnail, getFileSize} from "../../system/utils";
import api, {normalizeApiError} from "../../system/api";

const INITIAL_FILE_STATE = {
    progress: 0,
    status: "idle",
    error: ""
};

const getFileKey = (file) => (
    `${file?.name || ""}-${file?.size || 0}-${file?.type || ""}-${file?.lastModified || 0}`
);

const getErrorValue = (value) => {
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (Array.isArray(value)) return value.map(getErrorValue).filter(Boolean).join(" | ");

    if (value && typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch (error) {
            return "";
        }
    }

    return "";
};

export const getUploadErrorMessage = (error) => {
    const normalizedError = error?.normalizedError || normalizeApiError(error);
    const responseData = error?.response?.data;
    const backendError = responseData?.error;
    const validationErrors = backendError?.details?.errors || responseData?.errors || [];
    const messages = [];

    if (Array.isArray(validationErrors)) {
        validationErrors.forEach((validationError) => {
            const path = Array.isArray(validationError?.path)
                ? validationError.path.join(".")
                : getErrorValue(validationError?.path);
            const message = getErrorValue(validationError?.message || validationError?.name || validationError);

            if (message) messages.push(path ? `${path}: ${message}` : message);
        });
    }

    [
        typeof responseData === "string" ? responseData : "",
        typeof backendError === "string" ? backendError : backendError?.message,
        responseData?.message,
        backendError?.details?.message,
        normalizedError?.message
    ].forEach((message) => {
        const value = getErrorValue(message);
        if (value) messages.push(value);
    });

    const uniqueMessages = [...new Set(messages)];
    const errorCode = backendError?.details?.code || responseData?.code || normalizedError?.code;
    const metadata = [
        normalizedError?.status ? `HTTP ${normalizedError.status}` : "",
        backendError?.name,
        errorCode
    ].filter(Boolean).join(" / ");
    const details = uniqueMessages.join(" | ");

    if (metadata && details) return `${metadata}: ${details}`;
    return metadata || details;
};

const getUploadedFiles = (response, uploadMode) => {
    const payload = response?.data?.data ?? response?.data;

    if (uploadMode === "profile" && payload?.media) {
        return [{
            ...payload.media,
            url: payload.avatarUrl,
            avatarUrl: payload.avatarUrl
        }];
    }

    return Array.isArray(payload) ? payload : [payload].filter(Boolean);
};

function UploadDialog({
    visible,
    setVisible,
    updateContentType,
    multiple = false,
    uploadMode = "workspace",
    entityType = "workspace",
    entityDocumentId,
    purpose = "attachment"
}) {
    const translate = useTranslate();
    const fileUploadRef = useRef(null);
    const {workspaceId} = useSelector((state) => state.app);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [fileStates, setFileStates] = useState({});
    const [isDragging, setIsDragging] = useState(false);

    const hasSelectedFile = selectedFiles.length > 0;
    const isUploading = Object.values(fileStates).some(({status}) => status === "uploading");
    const isSuccessful = hasSelectedFile && selectedFiles.every((file) => (
        fileStates[getFileKey(file)]?.status === "success"
    ));
    const hasUploadableFile = selectedFiles.some((file) => (
        fileStates[getFileKey(file)]?.status !== "success"
    ));
    const isInteractionLocked = isUploading || isSuccessful;

    useEffect(() => {
        if (visible) {
            setSelectedFiles([]);
            setFileStates({});
            setIsDragging(false);
        }
    }, [visible]);

    const updateFileState = (file, nextState) => {
        const fileKey = getFileKey(file);

        setFileStates((currentStates) => {
            const currentFileState = {
                ...INITIAL_FILE_STATE,
                ...currentStates[fileKey]
            };
            const resolvedNextState = typeof nextState === "function"
                ? nextState(currentFileState)
                : nextState;

            return {
                ...currentStates,
                [fileKey]: {...currentFileState, ...resolvedNextState}
            };
        });
    };

    const handleHide = () => {
        setSelectedFiles([]);
        setFileStates({});
        setIsDragging(false);
        fileUploadRef.current?.clear();
        setVisible(false);
    };

    const handleChoose = () => {
        fileUploadRef.current?.getInput()?.click();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);

        if (isInteractionLocked || (!multiple && hasSelectedFile)) return;

        fileUploadRef.current?.onFileSelect(event);
    };

    const handleSelect = (event) => {
        setSelectedFiles(event.files);
        setFileStates((currentStates) => event.files.reduce((nextStates, file) => {
            const fileKey = getFileKey(file);
            nextStates[fileKey] = currentStates[fileKey] || INITIAL_FILE_STATE;
            return nextStates;
        }, {}));
    };

    const handleRemove = (event) => {
        const fileKey = getFileKey(event.file);

        setSelectedFiles((files) => files.filter((file) => getFileKey(file) !== fileKey));
        setFileStates((currentStates) => {
            const nextStates = {...currentStates};
            delete nextStates[fileKey];
            return nextStates;
        });
    };

    const uploadFile = async (file) => {
        const data = new FormData();
        const isProfileUpload = uploadMode === "profile";

        if (!isProfileUpload && !workspaceId) {
            updateFileState(file, {
                progress: 0,
                status: "error",
                error: translate("media.error.workspace-required")
            });
            return {successful: false, data: []};
        }

        data.append("files", file);
        data.append("fileInfo", JSON.stringify({name: file.name}));
        if (!isProfileUpload) {
            data.append("entityType", entityType);
            if (entityDocumentId) data.append("entityDocumentId", entityDocumentId);
            data.append("purpose", purpose);
        }
        updateFileState(file, {progress: 0, status: "uploading", error: ""});

        try {
            const response = await api.post(
                isProfileUpload ? "/users/me/profile-image" : "/workspace-media/upload",
                data,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        ...(!isProfileUpload && {workspace: workspaceId})
                    },
                    suppressGlobalErrorHandler: true,
                    onUploadProgress: (progressEvent) => {
                        const ratio = progressEvent.total
                            ? progressEvent.loaded / progressEvent.total
                            : progressEvent.progress;

                        if (!Number.isFinite(ratio)) return;

                        const progress = Math.min(99, Math.round(ratio * 100));
                        updateFileState(file, (currentFileState) => ({
                            progress: Math.max(currentFileState.progress, progress)
                        }));
                    }
                }
            );

            updateFileState(file, {progress: 100, status: "success", error: ""});

            return {
                successful: true,
                data: getUploadedFiles(response, uploadMode)
            };
        } catch (error) {
            const errorKey = error?.response?.data?.error?.message || error?.normalizedError?.message;
            const translatedError = errorKey ? translate(errorKey) : "";
            const message = errorKey && translatedError !== errorKey
                ? translatedError
                : getUploadErrorMessage(error);
            updateFileState(file, {progress: 0, status: "error", error: message});

            return {successful: false, data: []};
        }
    };

    const uploadHandler = async (event) => {
        const filesToUpload = event.files.filter((file) => (
            fileStates[getFileKey(file)]?.status !== "success"
        ));

        if (!filesToUpload.length) return [];

        const results = await Promise.all(filesToUpload.map(uploadFile));
        const uploadedFiles = results.flatMap((result) => result.data);
        const hasError = results.some((result) => !result.successful);

        if (uploadedFiles.length) updateContentType?.(uploadedFiles);

        if (!hasError) {
            await new Promise((resolve) => window.setTimeout(resolve, 600));
            setVisible(false);
        }

        return uploadedFiles;
    };

    const headerTemplate = ({chooseButton}) => (
        <>
            <div className="hidden" aria-hidden="true">
                {chooseButton}
            </div>
            <div
                className={`border-2 border-dashed border-round-2xl p-4 md:p-6 flex flex-column align-items-center justify-content-center text-center transition-all transition-duration-300 ${isDragging ? "border-primary surface-0" : "border-300 surface-50"}`}
                onDragEnter={(event) => {
                    event.preventDefault();
                    if (!isInteractionLocked && (multiple || !hasSelectedFile)) setIsDragging(true);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
            >
                <div className="w-4rem h-4rem border-circle bg-teal-50 text-teal-600 flex align-items-center justify-content-center mb-3 transition-transform transition-duration-300">
                    <Icon icon="mi mi:upload_file" className="text-3xl"/>
                </div>
                <span className="text-sm font-bold text-900 mb-2">
                    {translate("upload-files")}
                </span>
                <span id="upload-dialog-help" className="text-xs text-500 line-height-3 mb-4">
                    {translate("upload-file-help")}
                </span>
                <Button
                    type="button"
                    label={translate("choose-files")}
                    className="text-xs border-round-xl px-4 py-2"
                    onClick={handleChoose}
                    disabled={isInteractionLocked}
                    aria-describedby="upload-dialog-help"
                />
            </div>
        </>
    );

    const itemTemplate = (file, options) => {
        const fileSize = getFileSize(options.formatSize);
        const fileState = fileStates[getFileKey(file)] || INITIAL_FILE_STATE;

        return (
            <div className="pt-4">
                {options.index === 0 && (
                    <span className="block text-xs font-bold text-500 mb-2 px-2">
                        {translate("upload-files")}
                    </span>
                )}
                <div className="surface-card border-1 border-200 border-round-xl p-3 flex align-items-start justify-content-between gap-3 shadow-1">
                    <div className="flex align-items-start gap-3 min-w-0 flex-1">
                        <div className="w-3rem h-3rem flex-shrink-0 surface-50 border-1 border-200 border-round-lg flex align-items-center justify-content-center overflow-hidden" aria-hidden="true">
                            {fileThumbnail(file?.name, file.objectURL, "2.5rem")}
                        </div>
                        <div className="flex flex-column min-w-0 flex-1 text-right">
                            <div className="flex align-items-center justify-content-between gap-2 min-w-0">
                                <span className="text-xs font-bold text-900 white-space-nowrap text-overflow-ellipsis overflow-hidden min-w-0">
                                    {options.fileNameElement}
                                </span>
                                <div
                                    className="w-3rem h-3rem flex-shrink-0 flex align-items-center justify-content-center"
                                    role="progressbar"
                                    aria-live="polite"
                                    aria-atomic="true"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                    aria-valuenow={fileState.progress}
                                    aria-label={`${file.name} ${fileState.progress}%`}
                                >
                                    {fileState.status === "success" ? (
                                        <span className="w-2rem h-2rem border-circle bg-green-50 text-green-600 flex align-items-center justify-content-center scalein animation-duration-300">
                                            <i className="pi pi-check text-lg" aria-hidden="true"/>
                                        </span>
                                    ) : fileState.status === "error" ? (
                                        <span className="w-2rem h-2rem border-circle bg-red-50 text-red-600 flex align-items-center justify-content-center scalein animation-duration-300">
                                            <i className="pi pi-exclamation text-sm" aria-hidden="true"/>
                                        </span>
                                    ) : (
                                        <Knob
                                            value={fileState.progress}
                                            size={40}
                                            strokeWidth={8}
                                            valueTemplate="{value}%"
                                            valueColor="var(--primary-color)"
                                            rangeColor="var(--surface-border)"
                                            textColor="var(--text-color-secondary)"
                                            readOnly
                                            aria-hidden="true"
                                            pt={{label: {className: "text-3xs"}}}
                                        />
                                    )}
                                </div>
                            </div>
                            <span className="text-xs text-500">
                                {`${fileSize.size} ${translate(fileSize.unit)}`}
                            </span>
                            {fileState.error && (
                                <Message
                                    severity="error"
                                    text={fileState.error}
                                    className="w-full mt-2 text-xs justify-content-start"
                                    pt={{text: {className: "text-xs line-height-3 white-space-normal"}}}
                                />
                            )}
                        </div>
                    </div>
                    <Button
                        type="button"
                        text
                        rounded
                        severity="danger"
                        className="flex-shrink-0 shadow-none"
                        icon={<Icon icon="mi mi:close" className="text-lg"/>}
                        onClick={options.onRemove}
                        disabled={isInteractionLocked}
                        aria-label={translate("delete")}
                    />
                </div>
            </div>
        );
    };

    const footer = (
        <div className="surface-50 border-top-1 border-200 px-3 md:px-5 py-3 md:py-4 flex flex-column sm:flex-row align-items-stretch sm:align-items-center justify-content-between gap-2">
            <Button
                type="button"
                label={translate("upload")}
                icon={<Icon icon="mi mi:cloud_done" className="text-lg"/>}
                className="text-xs gap-2 border-round-xl px-4 py-2"
                onClick={() => fileUploadRef.current?.upload()}
                disabled={!hasSelectedFile || !hasUploadableFile || isInteractionLocked}
                loading={isUploading}
            />
            <Button
                type="button"
                label={translate("cancel")}
                text
                severity="secondary"
                className="text-xs border-round-xl px-4 py-2"
                onClick={handleHide}
                disabled={isSuccessful}
            />
        </div>
    );

    return (
        <Dialog
            className="w-full md:w-8 lg:w-6 xl:w-4 rtl border-round-2xl overflow-hidden"
            header={<span className="text-lg md:text-xl font-bold">{translate("upload-files")}</span>}
            footer={footer}
            visible={visible}
            onHide={handleHide}
            ariaCloseIconLabel={translate("cancel")}
            breakpoints={{"960px": "75vw", "640px": "calc(100vw - 2rem)"}}
            pt={{
                root: {className: "border-round-2xl overflow-hidden"},
                header: {className: "px-3 md:px-5 py-3 md:py-4 border-bottom-1 border-200"},
                closeButton: {className: "transition-colors transition-duration-200"},
                content: {className: "p-0"},
                footer: {className: "p-0"}
            }}
        >
            <div className="p-3 md:p-5">
                <FileUpload
                    ref={fileUploadRef}
                    customUpload
                    multiple={multiple}
                    uploadHandler={uploadHandler}
                    headerTemplate={headerTemplate}
                    itemTemplate={itemTemplate}
                    emptyTemplate={() => null}
                    progressBarTemplate={() => null}
                    onSelect={handleSelect}
                    onRemove={handleRemove}
                    onClear={() => {
                        setSelectedFiles([]);
                        setFileStates({});
                    }}
                    disabled={isInteractionLocked}
                    className="border-none"
                    contentClassName="p-0 border-none"
                    pt={{
                        root: {className: "border-none"},
                        content: {className: "border-none surface-card"}
                    }}
                />
            </div>
        </Dialog>
    );
}

export default UploadDialog;
